import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, ExternalLink } from 'lucide-react';
import { LangContext } from '../App';
import { translations } from '../lib/translations';

const MAP_URL =
  'https://www.google.com/maps/search/?api=1&query=Rua+Dr.+Albino+José+da+Silva+nº+31,+4830-575+Póvoa+de+Lanhoso';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
};

export default function Contact() {
  const lang = useContext(LangContext);
  const t = translations[lang].contact;

  const items = [
    {
      Icon: Phone,
      label: t.phoneLabel,
      primary: t.phone,
      href: `tel:+351${t.phone.replace(/\s/g, '')}`,
      testId: 'contact-phone',
    },
    {
      Icon: Mail,
      label: t.emailLabel,
      primary: t.email,
      href: `mailto:${t.email}`,
      testId: 'contact-email',
    },
    {
      Icon: MapPin,
      label: t.addressLabel,
      primary: t.address,
      secondary: t.addressCity,
      href: MAP_URL,
      external: true,
      testId: 'contact-address',
    },
    {
      Icon: Clock,
      label: t.hoursLabel,
      primary: t.hours,
      secondary: t.hoursTime,
      testId: 'contact-hours',
    },
  ];

  return (
    <section
      id="contact"
      className="py-28 bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-20"
          {...fadeUp}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#6B2D3E] mb-5">
            — {t.sectionLabel} —
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] tracking-tight mb-4">
            {t.title}
          </h2>
          <p className="font-sans text-base text-[#4A4A4A]">{t.subtitle}</p>
        </motion.div>

        {/* Large Phone Hero */}
        <motion.div
          className="text-center mb-20"
          {...fadeUp}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          data-testid="contact-phone-hero"
        >
          <a
            href={`tel:+351919947343`}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1A1A1A] tracking-tight hover:text-[#6B2D3E] transition-colors duration-400 block"
            data-testid="contact-phone-link"
          >
            919 947 343
          </a>
          <div className="w-12 h-px bg-[#6B2D3E] mx-auto mt-6" />
        </motion.div>

        {/* Contact Cards */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        >
          {items.map(({ Icon, label, primary, secondary, href, external, testId }, i) => (
            <motion.div
              key={i}
              className="text-center lg:text-left"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={testId}
            >
              <Icon
                size={18}
                className="text-[#6B2D3E] mb-4 mx-auto lg:mx-0"
                strokeWidth={1.5}
              />
              <p className="font-sans text-xs tracking-widest uppercase text-[#AAAAAA] mb-2">
                {label}
              </p>
              {href ? (
                <a
                  href={href}
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noopener noreferrer' : undefined}
                  className="font-sans text-sm text-[#1A1A1A] hover:text-[#6B2D3E] transition-colors duration-300 leading-relaxed block"
                  data-testid={`${testId}-link`}
                >
                  {primary}
                  {secondary && <><br />{secondary}</>}
                  {external && (
                    <ExternalLink size={11} className="inline-block ml-1 opacity-50" />
                  )}
                </a>
              ) : (
                <div className="font-sans text-sm text-[#1A1A1A] leading-relaxed">
                  {primary}
                  {secondary && <><br />{secondary}</>}
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
